import { useState, useEffect } from 'react';

/** Last token on frame id/name is usually the catalogue code (e.g. A81903, AR24OAK). */
export function getFrameProductCode(frame) {
  if (!frame) return null;
  const raw = String(frame.id || frame.name || '').trim();
  if (!raw) return null;
  const parts = raw.split(/\s+/).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const token = parts[i].replace(/[^A-Za-z0-9]/g, '');
    if (/^[A-Za-z]{1,4}\d/.test(token) || /^[A-Z]{2,}\d*[A-Z]+$/.test(token)) {
      return token;
    }
  }
  return null;
}

/** Parse "45w x 56h x 30rw" style tokens from frame marketing name. */
export function parseFrameProfileFromName(name) {
  if (!name) return {};
  const wHrW = name.match(/(\d+)\s*w\s*x\s*(\d+)\s*h(?:\s*x\s*(\d+)\s*rw)?/i);
  const rebateH = name.match(/(\d+)\s*rh\b/i);
  return {
    widthMm: wHrW ? wHrW[1] : null,
    heightMm: wHrW ? wHrW[2] : null,
    rebateWidthMm: wHrW && wHrW[3] ? wHrW[3] : null,
    rebateHeightMm: rebateH ? rebateH[1] : null
  };
}

function deriveColourFromName(name) {
  if (!name) return '—';
  const n = name.trim();
  const pairs = [
    ['Gold & Black', 'Gold & Black'],
    ['Silver & Black', 'Silver & Black'],
    ['Natural Oak', 'Natural Oak'],
    ['Anthracite', 'Anthracite'],
    ['Chocolate', 'Chocolate'],
    ['Black', 'Black'],
    ['White', 'White'],
    ['Grey', 'Grey'],
    ['Gray', 'Grey']
  ];
  for (const [prefix, label] of pairs) {
    if (n.toLowerCase().startsWith(prefix.toLowerCase())) return label;
  }
  const first = n.split(/\s+/)[0];
  return first || '—';
}

function deriveStyleFromName(name) {
  if (!name) return '—';
  if (/floating/i.test(name)) return 'Floating';
  if (/\bbox\b/i.test(name)) return 'Box';
  if (/\bflat\b/i.test(name)) return 'Flat';
  return 'Contemporary';
}

function buildSpecRows(frame) {
  const parsed = parseFrameProfileFromName(frame.name);
  const height =
    parsed.heightMm != null ? `${parsed.heightMm} mm` : `${frame.height ?? '—'} mm`;
  const width =
    parsed.widthMm != null ? `${parsed.widthMm} mm` : `${frame.width ?? '—'} mm`;
  const rebateW =
    parsed.rebateWidthMm != null ? `${parsed.rebateWidthMm} mm` : '—';
  const rebateH =
    parsed.rebateHeightMm != null
      ? `${parsed.rebateHeightMm} mm`
      : frame.thickness != null
        ? `${frame.thickness} mm`
        : '—';

  return [
    { label: 'Height', value: height },
    { label: 'Width', value: width },
    { label: 'Rebate Height', value: rebateH },
    { label: 'Rebate Width', value: rebateW },
    { label: 'Style', value: deriveStyleFromName(frame.name) },
    { label: 'Colour', value: deriveColourFromName(frame.name) }
  ];
}

/**
 * Profile stick + SPECIFICATIONS block (reference layout: narrow image | spec list).
 * Stick files: `/assets/Sticks/{code}.jpg` under `public/assets/Sticks/`.
 */
export default function FrameStickSpecPanel({ frame }) {
  const [stickLoaded, setStickLoaded] = useState(true);
  const code = getFrameProductCode(frame);
  const stickSrc = code ? `/assets/Sticks/${code}.jpg` : null;

  useEffect(() => {
    setStickLoaded(!!stickSrc);
  }, [stickSrc]);

  if (!frame) return null;

  const rows = buildSpecRows(frame);

  return (
    <section className="frame-stick-spec-panel" aria-label="Frame profile and specifications">
      <div className="frame-stick-spec-panel__inner">
        <div className="frame-stick-spec-panel__image-box">
          {stickSrc && stickLoaded ? (
            <img
              src={stickSrc}
              alt=""
              className="frame-stick-spec-panel__stick-img"
              onError={() => setStickLoaded(false)}
            />
          ) : (
            <span className="frame-stick-spec-panel__image-fallback" aria-hidden="true">
              —
            </span>
          )}
        </div>
        <div className="frame-stick-spec-panel__spec-box">
          <h3 className="frame-stick-spec-panel__heading">SPECIFICATIONS</h3>
          <div className="frame-stick-spec-panel__list">
            {rows.map(({ label, value }) => (
              <div key={label} className="frame-stick-spec-panel__row">
                <span className="frame-stick-spec-panel__label">{label}:</span>
                <span className="frame-stick-spec-panel__value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
